/**
 * Mobile Dynasty Table Service
 * Simplified table rendering for mobile with 3-line format support
 * Based on desktop DynastyTable but optimized for touch and smaller screens
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { ChevronUp, ChevronDown, ChevronRight } from 'lucide-react';
import { dynastyTheme, withAlpha } from '../../../services/colorService';
import { mobileTableStyles, mobileColors } from '../../../services/mobileStyleService';
import { formatBattingAvg } from '../../../services/statFormatUtils';
import { getNestedValue } from '../../../utils/getNestedValue';

const C = dynastyTheme.tokens.colors;

export { getNestedValue };

// =============================================================================
// STICKY CONTEXT COLUMNS (prop A — generalized frozen leading columns)
// =============================================================================
// A column joins the frozen/sticky context group if it EITHER opts in via the
// generalized `stickyContext: true` flag (any number of leading context
// columns, e.g. box score's POS + Player + Tm), OR via the LEGACY `sticky: true`
// on the canonical POS/Player keys. The legacy predicate is preserved verbatim
// so the four existing consumers (Roster, Team Stats, Live Draft, FA) render
// byte-for-byte unchanged — including FA bulk-mode's `checkbox` column, which
// carries a latent `sticky: true` that has always been a no-op (key not in the
// whitelist) and MUST remain non-sticky.
export const isStickyCol = (col) =>
  col.stickyContext === true ||
  Boolean(col.sticky && (col.key === 'position_slot' || col.key === 'player_name'));

// =============================================================================
// OVERFLOW AFFORDANCE (prop C — hidden scrollbars + inline › / ⌄ arrows)
// =============================================================================
// Pure, testable measurement: given a scroll element (or any object exposing
// the scroll geometry), report whether more content exists to the right / below.
export const computeOverflow = (el) => {
  if (!el) return { right: false, down: false };
  const right = (el.scrollWidth - el.clientWidth - el.scrollLeft) > 1;
  const down = (el.scrollHeight - el.clientHeight - el.scrollTop) > 1;
  return { right, down };
};

// Cross-browser scrollbar hiding (webkit needs a real stylesheet rule; Firefox
// + legacy Edge are handled inline via scrollbarWidth / msOverflowStyle).
const MDT_SCROLLBAR_CSS = '.mobile-dynasty-scroll::-webkit-scrollbar{display:none;width:0;height:0;}';

// Softened gold hairline that divides each player's row-block. Declared on every
// <td> of the block's FIRST row (incl. the frozen sticky cells) so it spans full
// width under POS/Player; pairs with removing the Accrued row's bottom border so
// border-collapse can't hide it. 1px gold @ 50% — NOT a heavy solid bar.
const GROUP_DIVIDER = `1px solid ${withAlpha(C.goldBase, 0.5)}`;

const overflowArrowBase = {
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
  zIndex: 25,
  color: dynastyTheme.tokens.colors.primary,
  opacity: 0.9,
};
const overflowArrowRightStyle = {
  ...overflowArrowBase,
  right: 0,
  top: '50%',
  transform: 'translateY(-50%)',
  height: '100%',
  paddingRight: '1px',
  background: `linear-gradient(90deg, ${withAlpha(C.ground.screen, 0)} 0%, ${withAlpha(C.ground.screen, 0.85)} 70%)`,
  width: '18px',
};
const overflowArrowDownStyle = {
  ...overflowArrowBase,
  bottom: 0,
  left: '50%',
  transform: 'translateX(-50%)',
  width: '100%',
  paddingBottom: '1px',
  background: `linear-gradient(180deg, ${withAlpha(C.ground.screen, 0)} 0%, ${withAlpha(C.ground.screen, 0.85)} 70%)`,
  height: '16px',
};

// =============================================================================
// RENDER HELPERS (from DynastyTable)
// =============================================================================
export const renderDefault = (v) => v || 0;

export const renderFloat1 = (v) => {
  const val = parseFloat(v) || 0;
  if (val === 0) return '.0';
  const formatted = val.toFixed(1);
  return val < 1 && val > 0 ? formatted.substring(1) : formatted;
};

export const renderFloat2 = (v) => {
  const val = parseFloat(v) || 0;
  if (val === 0) return '.00';
  const formatted = val.toFixed(2);
  return val < 1 && val > 0 ? formatted.substring(1) : formatted;
};

export const renderFloat3 = (v) => {
  return formatBattingAvg(v);
};

export const renderAvg = (v) => {
  return formatBattingAvg(v);
};

// =============================================================================
// MOBILE DYNASTY TABLE COMPONENT
// =============================================================================

export const MobileDynastyTable = ({
  data = [],
  columns = [],
  keyField = 'id',
  onRowClick,
  maxHeight = null,
  className = '',
  stickyHeader = true,
  showBorder = true,
  // 'zebra' (default, per-group striping) | 'borderless' (no zebra: flat row +
  // faint inter-row hairline + slightly-shaded header). Token-driven — see
  // mobileTableStyles.borderless.
  rowVariant = 'zebra',
  // Opt-in header text style merged onto each header label (e.g. Game Logs gold
  // 8px/800 uppercase). Null → the default white header.
  // Opt-in header text style, reused for the header AND the totals row (so the
  // totals render header-formatted: gold + weight). Null → default white header.
  headerStyle = null,
  // NEW PROPS FOR TABLE TYPES AND RENDERING MODES (matching DynastyTable)
  tableType = 'player-list',  // 'roster-lineup' | 'player-list' | 'flat-data'
  leaguePositions = null,     // For roster-lineup: {hitting: [...], pitching: [...]}
  playerType = null,          // For roster-lineup: 'hitting' | 'pitching'
  rowsPerItem = 1,            // How many rows per item (1, 2, or 3)
  rowTypes = ['season'],       // ['season'] | ['accrued'] | ['season', 'rolling'] | etc.
  showTotals = false,
  totalsRow = null,
  // Opt-in player-row bottom-divider override (full border string). Null → the
  // default gray house hairline. The Box Score (BoxMini) passes a softened gold
  // hairline; roster/stats/etc. keep the default.
  rowDivider = null,
  initialSort = null,  // { key: 'columnKey', direction: 'asc' | 'desc' }
  // Custom row styling functions
  getRowStyle = null,  // (item, itemIndex, rowType, rowIndex) => style object
  getRowClassName = null  // (item, itemIndex, rowType, rowIndex) => className string
}) => {
  const [sortConfig, setSortConfig] = useState(initialSort || { key: null, direction: null });
  
  // Generate position slots for roster-lineup tables (from DynastyTable)
  const generatePositionSlots = (positionArray) => {
    if (!positionArray || !Array.isArray(positionArray)) return [];
    
    const slots = [];
    const positionCounts = {};
    
    positionArray.forEach(pos => {
      const count = positionCounts[pos] || 0;
      slots.push({
        slotPosition: pos,           // Display value (e.g., 'C')
        slotId: `${pos}_${count}`,  // Assignment value (e.g., 'C_0')
        player: null                 // Will be filled by matching logic
      });
      positionCounts[pos] = count + 1;
    });
    
    return slots;
  };

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;

    const sorted = [...data].sort((a, b) => {
      const column = columns.find(col => col.key === sortConfig.key);
      // sortValue is preferred — it operates on raw row items (e.g.,
      // FA Search passes Array<Player>; buildDynamicStatColumn's
      // wrapped-row accessor returns undefined for those). Falls
      // through to accessor (wrapped-row tables) then getNestedValue.
      const aVal = column?.sortValue ? column.sortValue(a)
                   : column?.accessor ? column.accessor(a)
                   : getNestedValue(a, sortConfig.key);
      const bVal = column?.sortValue ? column.sortValue(b)
                   : column?.accessor ? column.accessor(b)
                   : getNestedValue(b, sortConfig.key);

      if (aVal === bVal) return 0;
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      const comparison = aVal < bVal ? -1 : 1;
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [data, sortConfig, columns]);

  // Handle sort
  const handleSort = (columnKey) => {
    const column = columns.find(col => col.key === columnKey);
    if (!column?.sortable) return;

    setSortConfig(prev => {
      if (prev.key !== columnKey) return { key: columnKey, direction: 'desc' };
      if (prev.direction === 'desc') return { key: columnKey, direction: 'asc' };
      return { key: null, direction: null };
    });
  };

  // Prop A: the frozen sticky context columns, in array order, for cumulative
  // left-offset stacking and last-sticky (shadow) detection.
  const stickyColumns = useMemo(() => columns.filter(isStickyCol), [columns]);

  // Prop C: overflow affordance. Measure on mount, on scroll, on container or
  // content resize (ResizeObserver), and on window resize — never a one-shot.
  const scrollRef = useRef(null);
  const [overflow, setOverflow] = useState({ right: false, down: false });
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const measure = () => {
      const next = computeOverflow(el);
      setOverflow((prev) =>
        prev.right === next.right && prev.down === next.down ? prev : next
      );
    };
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    if (typeof window !== 'undefined') window.addEventListener('resize', measure);
    return () => {
      el.removeEventListener('scroll', measure);
      if (ro) ro.disconnect();
      if (typeof window !== 'undefined') window.removeEventListener('resize', measure);
    };
  }, [data, columns, maxHeight, rowTypes, showTotals]);

  const borderless = rowVariant === 'borderless';
  // Fill mode (maxHeight==='100%'): the table must fill its flex parent so the
  // scroll container has a CONCRETE height to clip against — without that, sticky
  // bottom:0 (totals) has no edge to pin to. The wrapper + scroll flex to fill;
  // the height chain is supplied by the consumer (a flex-column parent). A fixed
  // px maxHeight is already concrete and needs none of this.
  const fillHeight = maxHeight === '100%';
  // A real height cap (px value or '100%' fill) — NOT null/'none'. Only then do we
  // turn on vertical scroll; uncapped tables keep overflow-y:visible unchanged.
  const hasCap = !!maxHeight && maxHeight !== 'none';

  return (
    <div
      className="mobile-dynasty-table-wrapper"
      style={fillHeight
        ? { position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }
        : { position: 'relative' }}
    >
      {/* Prop C: webkit scrollbar hiding (Firefox/Edge handled inline below) */}
      <style>{MDT_SCROLLBAR_CSS}</style>
      <div
        ref={scrollRef}
        className={`mobile-dynasty-scroll overflow-x-auto ${className}`}
        style={{
          // overflowY:auto is REQUIRED for a height cap to clip vertically —
          // without it the table overflows its box and the sticky-bottom totals
          // (and sticky header) pin to the OUTER scroller, visible only at full
          // scroll. Root of the off-screen-totals bug (modal 340px + profile fill).
          // Only when a cap is set; uncapped ('none'/null) stays visible.
          overflowY: hasCap ? 'auto' : 'visible',
          // Fill: take the wrapper's full (concrete) height so it always clips and
          // the totals pin at the visible bottom. Fixed: cap at the px maxHeight.
          ...(fillHeight ? { flex: 1, minHeight: 0 } : { maxHeight: maxHeight }),
          WebkitOverflowScrolling: 'touch',
          isolation: 'isolate',  // Create stacking context for sticky columns
          scrollbarWidth: 'none',     // Firefox: hide scrollbar
          msOverflowStyle: 'none',    // legacy Edge/IE: hide scrollbar
          margin: 0,
          padding: 0
        }}
      >
      <table
        className="mobile-dynasty-table"
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '11px',
          tableLayout: 'fixed',  // Force column widths to be respected
          display: 'table',  // Ensure table display
          margin: 0,
          padding: 0
        }}>
        {/* Header.
            zIndex hierarchy at the sticky-top + sticky-left intersection:
              30 — double-sticky <th> (POS / Player header cells)
              20 — sticky-top <th> (other header cells; inherits from thead)
              10 — sticky-left <td> (POS / Player data cells)
              default — normal <td>
            This keeps the header floating above frozen-column data cells
            when the user scrolls vertically; before the bump, frozen <td>
            at z 10 painted OVER the thead at z 10 via DOM order. */}
        <thead style={{
          position: stickyHeader ? 'sticky' : 'relative',
          top: 0,
          zIndex: 20,
          backgroundColor: borderless ? mobileTableStyles.borderless.header : mobileTableStyles.header.background,
          // Yellow underline on the THEAD (not per-th) so it scrolls as a
          // single continuous line with the sticky header. Previously each
          // <th> had its own borderBottom; the sticky-corner <th> at
          // z-index 30 painted a separate background that broke the
          // continuity at the column boundary during scroll.
          borderBottom: borderless ? mobileTableStyles.borderless.divider : `1px solid ${dynastyTheme.tokens.colors.primary}`
        }}>
          <tr>
            {columns.map((column, idx) => {
              // Sticky context column? (generalized via isStickyCol — legacy
              // POS/Player keys OR opt-in stickyContext; see helper above).
              const isStickyColumn = isStickyCol(column);
              const isFirstSticky = column.key === 'position_slot';
              const isSecondSticky = column.key === 'player_name';
              const stickyIndex = isStickyColumn ? stickyColumns.indexOf(column) : -1;
              // The shadow at the frozen/scroll boundary belongs on the LAST
              // sticky column. For the legacy 2-col case last === player_name ===
              // isSecondSticky, and for Live Draft's lone player_name it is also
              // the last — so this is byte-for-byte vs the prior isSecondSticky.
              const isLastSticky = isStickyColumn && stickyIndex === stickyColumns.length - 1;

              // Left offset = cumulative width of preceding sticky columns.
              // Legacy player_name path is preserved verbatim (incl. the 32px
              // default when POS is absent — Live Draft). Generalized
              // stickyContext columns stack on the full preceding sticky run.
              let leftOffset = 0;
              if (isSecondSticky) {
                const posColumn = columns.find(col => col.key === 'position_slot');
                const posWidthStr = posColumn?.width || '32px';
                const posWidth = parseInt(posWidthStr);
                // Player column starts exactly at POS column width (border is inside POS column)
                leftOffset = posWidth;
              } else if (column.stickyContext === true) {
                for (let i = 0; i < stickyIndex; i++) {
                  leftOffset += parseInt((stickyColumns[i].width || '0px').toString(), 10) || 0;
                }
              }

              return (
              <th
                key={column.key || idx}
                onClick={() => column.sortable && handleSort(column.key)}
                onTouchEnd={(e) => {
                  // Support touch events for mobile sorting
                  e.preventDefault();
                  if (column.sortable) {
                    handleSort(column.key);
                  }
                }}
                style={{
                  // Symmetric vertical padding + verticalAlign:middle so the column-
                  // header text is vertically CENTERED in its row (matching the
                  // TEAM TOTALS row), NOT top-justified.
                  padding: isStickyColumn ? '4.8px 0px' : '4.8px 2.4px', // No left/right padding on sticky columns
                  verticalAlign: 'middle',
                  textAlign: column.textAlign || 'center',
                  fontWeight: '700',
                  fontSize: '10px',
                  lineHeight: '1.0',
                  color: dynastyTheme.tokens.colors.neutral[400],
                  // Uniform background on every <th> (sticky-corner AND
                  // normal) so the thead's yellow borderBottom appears as
                  // ONE continuous line, not interrupted by mismatched per-th
                  // backgrounds. The isStickyColumn branch below extends this
                  // with sticky positioning but does NOT re-declare bg.
                  backgroundColor: borderless ? mobileTableStyles.borderless.header : mobileTableStyles.header.background,
                  cursor: column.sortable ? 'pointer' : 'default',
                  whiteSpace: 'nowrap',
                  // Per-cell sticky-top (robust across mobile webkit where a
                  // sticky <thead>/<tr> alone can fail). zIndex 20 keeps the
                  // header above scrolling rows; the sticky-column branch below
                  // bumps the corner to 30. Pairs with the sticky totals row.
                  position: 'sticky',
                  top: 0,
                  zIndex: 20,
                  ...(column.width ? { 
                    width: column.width.toString().endsWith('px') ? column.width : `${column.width}px`,
                    minWidth: column.width.toString().endsWith('px') ? column.width : `${column.width}px`,
                    maxWidth: column.width.toString().endsWith('px') ? column.width : `${column.width}px`
                  } : {}),
                  ...(column.minWidth ? { 
                    minWidth: column.minWidth.toString().endsWith('px') ? column.minWidth : `${column.minWidth}px`
                  } : {}),
                  ...(column.maxWidth ? { 
                    maxWidth: column.maxWidth.toString().endsWith('px') ? column.maxWidth : `${column.maxWidth}px`
                  } : {}),
                  // Sticky positioning for POS and Player columns
                  // (double-sticky: both top:0 from thead AND left:0/leftOffset
                  // here). zIndex 30 puts these above the thead's zIndex 20
                  // so the corner cell sits on top of both other sticky
                  // regions during scroll. Background is inherited from the
                  // uniform <th> bg above — NOT re-declared here, so the
                  // thead's yellow borderBottom remains a single continuous
                  // line across all header cells during scroll.
                  ...(isStickyColumn ? {
                    position: 'sticky',
                    left: isFirstSticky ? 0 : `${leftOffset}px`,
                    zIndex: 30,
                    borderRight: 'none', // No vertical line between POS and Player
                    boxShadow: isLastSticky ? `2px 0 4px ${withAlpha(C.black, 0.3)}` : 'none',
                    marginLeft: 0,
                    paddingLeft: 0, // Remove ALL left padding to eliminate gap
                    paddingRight: isSecondSticky ? 0 : undefined,
                    opacity: 1, // Ensure fully opaque
                    backgroundClip: 'padding-box' // Prevent background bleeding
                  } : {})
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: (isStickyColumn && isSecondSticky) ? 'flex-start' : (column.textAlign === 'left' ? 'flex-start' : column.textAlign === 'right' ? 'flex-end' : 'center'),
                  gap: '4px',
                  padding: 0,
                  margin: 0
                }}>
                  <span style={{ color: C.white, ...(headerStyle || {}) }}>{column.title || column.header}</span>
                  {column.sortable && sortConfig.key === column.key && (
                    sortConfig.direction === 'desc' ? (
                      <ChevronDown size={12} color={dynastyTheme.tokens.colors.primary} />
                    ) : (
                      <ChevronUp size={12} color={dynastyTheme.tokens.colors.primary} />
                    )
                  )}
                </div>
              </th>
              );
            })}
          </tr>
        </thead>

        {/* Body */}
        <tbody>
          {(() => {
            // STEP 1: Prepare items to render based on table type
            let itemsToRender;
            
            if (tableType === 'roster-lineup') {
              // Type 1: Generate position slots and match players
              const slots = generatePositionSlots(leaguePositions?.[playerType]);
              
              // Match players to slots based on roster.position
              slots.forEach(slot => {
                slot.player = sortedData.find(p => p.league_context?.roster?.position === slot.slotId) || null;
              });
              
              itemsToRender = slots;
              
            } else if (tableType === 'player-list') {
              // Type 2: Simple player list - wrap in consistent structure
              itemsToRender = sortedData.map(player => ({ player }));
              
            } else if (tableType === 'flat-data') {
              // Type 3: Non-player data (draft picks, ledger, etc.)
              itemsToRender = sortedData.map(row => ({ flatData: row }));
            } else {
              itemsToRender = sortedData.map(row => ({ flatData: row }));
            }
            
            // STEP 2: Render N rows per item based on rowsPerItem
            return itemsToRender.map((item, itemIndex) => {
              // Generate rows for this item
              return rowTypes.map((rowType, rowIndex) => {
                const key = `${itemIndex}-${rowType}`;
                const isClickable = !!onRowClick;
                
                // Yellow line ONLY on accrued row - NEVER on season or rolling rows
                // Player Name row = season row = NO yellow line
                // 14-Day row = rolling row = NO yellow line  
                // Accrued row = accrued row = YES yellow line
                // CRITICAL: Use strict equality and explicit 'none' to prevent any CSS inheritance
                const isAccruedRow = rowType === 'accrued';
                
                // Default row styling
                const defaultTrStyle = {
                  cursor: isClickable ? 'pointer' : 'default',
                  backgroundColor: borderless ? mobileTableStyles.borderless.row : (itemIndex % 2 === 0 ? mobileTableStyles.rows.even : mobileTableStyles.rows.odd),
                  // Bug A: the Accrued (last) row carries NO bottom border — it used
                  // to win the border-collapse against the next group's gold divider
                  // and hide it. The group divider now lives on the next block's
                  // first row as a per-cell border-top (Bug B, below).
                  borderBottom: borderless ? mobileTableStyles.borderless.divider : (isAccruedRow ? 'none' : (rowDivider || mobileTableStyles.rows.border)),
                  boxShadow: 'none',
                  height: '22px',
                  minHeight: '22px',
                  maxHeight: '22px',
                  lineHeight: '1.0',
                  display: 'table-row'
                };
                
                // Allow parent to override row styling via getRowStyle prop
                const trStyle = getRowStyle 
                  ? getRowStyle(item, itemIndex, rowType, rowIndex, defaultTrStyle)
                  : defaultTrStyle;
                
                // Extract backgroundColor from trStyle for sticky columns.
                // Sticky cells need opaque bg (CSS can't do rgba on sticky),
                // so we MAP the row's effective stripe color to its opaque
                // counterpart. Previous logic derived sticky bg independently
                // from `itemIndex % 2`, which diverged from getRowStyle's
                // per-visual-row indexing (e.g., MobilePlayerTable's
                // globalRowIndex = itemIndex * 2 + rowIndex). That misalignment
                // produced stripes that didn't line up across frozen vs
                // scrolling columns. Now sticky bg follows whatever the row's
                // bg actually is.
                const rowBgColor = trStyle.backgroundColor || defaultTrStyle.backgroundColor;
                const stickyBgColor = borderless
                  ? mobileTableStyles.borderless.row
                  : (rowBgColor === mobileTableStyles.rows.even
                    ? mobileTableStyles.sticky.even
                    : mobileTableStyles.sticky.odd);
                
                return (
                  <tr
                    key={key}
                    data-row-type={rowType}
                    data-is-accrued={isAccruedRow}
                    onClick={(e) => {
                      // Only trigger row click if the click wasn't on a button or interactive element
                      if (isClickable && onRowClick && !e.target.closest('button') && !e.target.closest('input')) {
                        onRowClick(item);
                      }
                    }}
                    style={trStyle}
                  >
                    {columns.map((column, colIdx) => {
                      // Determine content based on table type
                      let cellContent;
                      
                      if (tableType === 'flat-data') {
                        // Type 3: Non-player data uses standard signature
                        const flatData = item.flatData;
                        const cellValue = column.accessor
                          ? column.accessor(flatData)
                          : getNestedValue(flatData, column.key);
                        cellContent = column.render 
                          ? column.render(cellValue, flatData, itemIndex)
                          : (cellValue ?? '-');
                      } else {
                        // Types 1 & 2: Player data uses new signature
                        const player = item.isTotalsRow ? null : item.player;
                        
                        // Support both desktop format (render(player, rowType, item)) and mobile format (render(value, row))
                        if (column.render) {
                          // Check if column uses mobile format (has accessor and expects (value, row))
                          if (column.accessor) {
                            // Mobile format: call accessor first, then render with (value, row)
                            const rowData = { 
                              ...item, 
                              statType: rowType, 
                              player: player,
                              rowsPerItem: rowsPerItem  // Pass rowsPerItem to help detect 3-line vs 1-line mode
                            };
                            const cellValue = column.accessor(rowData);
                            cellContent = column.render(cellValue, rowData);
                          } else {
                            // Desktop format: render(player, rowType, item)
                            cellContent = column.render(player, rowType, item);
                          }
                        } else {
                          // No render function - use accessor or getNestedValue
                          if (column.accessor) {
                            const rowData = { 
                              ...item, 
                              statType: rowType, 
                              player: player,
                              rowsPerItem: rowsPerItem  // Pass rowsPerItem to help detect 3-line vs 1-line mode
                            };
                            cellContent = column.accessor(rowData);
                          } else {
                            cellContent = player ? getNestedValue(player, column.key) : null;
                          }
                        }
                      }

                      // Sticky context column? (generalized — see isStickyCol)
                      const isStickyColumn = isStickyCol(column);
                      const isFirstSticky = column.key === 'position_slot';
                      const isSecondSticky = column.key === 'player_name';
                      const stickyIndex = isStickyColumn ? stickyColumns.indexOf(column) : -1;
                      const isLastSticky = isStickyColumn && stickyIndex === stickyColumns.length - 1;

                      // Left offset = cumulative width of preceding sticky columns
                      // (legacy player_name path preserved verbatim incl. 32px default).
                      let leftOffset = 0;
                      if (isSecondSticky) {
                        const posColumn = columns.find(col => col.key === 'position_slot');
                        const posWidthStr = posColumn?.width || '32px';
                        const posWidth = parseInt(posWidthStr);
                        // Player column starts exactly at POS column width (border is inside POS column)
                        leftOffset = posWidth;
                      } else if (column.stickyContext === true) {
                        for (let i = 0; i < stickyIndex; i++) {
                          leftOffset += parseInt((stickyColumns[i].width || '0px').toString(), 10) || 0;
                        }
                      }

                      // Determine text color: 
                      // - Stats in rolling row (2nd row) should be yellow
                      // - Stats in accrued row should be success color (green)
                      // - POS/Player/Team should be white
                      const isStatColumn = !['position_slot', 'player_name', 'team'].includes(column.key);
                      let textColor = C.white;
                      if (isStatColumn) {
                        if (rowType === 'rolling') {
                          textColor = mobileColors.rollingText; // 14-day / rolling row (house token)
                        } else if (rowType === 'accrued' || !rowType) {
                          textColor = mobileColors.accruedText; // Accrued row (house token)
                        }
                      }
                      
                      return (
                        <td
                          key={`${key}-${column.key || colIdx}`}
                          style={{
                            padding: isStickyColumn ? '0px' : (column.cellPadding || '1px 2.4px'), // Minimal padding to fit 24px height
                            position: isStickyColumn ? 'relative' : 'static', // Create positioning context for absolute inner div
                            height: '22px', // Fixed height for uniform rows (1-line and 3-line)
                            minHeight: '22px', // Ensure minimum height
                            maxHeight: '22px', // Prevent expansion
                            lineHeight: '1.0', // Tighter line height to fit 24px
                            display: 'table-cell', // Ensure table cell display
                            // Bug B: full-width gold group divider on EVERY cell of
                            // the block's first row (rowIndex 0 = Season in 3-line, or
                            // the single row in 1-line Totals mode). Declared per-cell
                            // so it spans under the frozen POS/Player sticky columns
                            // too — a <tr>-only border-top is painted over by the
                            // sticky cells' solid bg.
                            ...(rowIndex === 0 && !borderless ? { borderTop: GROUP_DIVIDER } : {}),
                            textAlign: column.textAlign || 'center',
                            color: textColor,
                            whiteSpace: column.allowOverflow ? 'normal' : 'nowrap',
                            overflow: column.allowOverflow ? 'visible' : 'hidden',
                            textOverflow: 'ellipsis',
                            fontSize: column.fontSize || '11px',
                            lineHeight: '1.0',
                            ...(column.width ? { 
                              width: column.width.toString().endsWith('px') ? column.width : `${column.width}px`,
                              minWidth: column.width.toString().endsWith('px') ? column.width : `${column.width}px`,
                              maxWidth: column.width.toString().endsWith('px') ? column.width : `${column.width}px`
                            } : {}),
                            ...(column.minWidth ? { 
                              minWidth: column.minWidth.toString().endsWith('px') ? column.minWidth : `${column.minWidth}px`
                            } : {}),
                            ...(column.maxWidth ? { 
                              maxWidth: column.maxWidth.toString().endsWith('px') ? column.maxWidth : `${column.maxWidth}px`
                            } : {}),
                            // Sticky positioning for POS and Player columns.
                            // borderBottom: 'none' on sticky cells so the sticky
                            // header's yellow underline isn't visually overlaid
                            // by the gray row border at the column boundary
                            // during scroll. Non-sticky <td> keep the default
                            // gray borderBottom from the <tr> defaultTrStyle.
                            ...(isStickyColumn ? {
                              position: 'sticky',
                              left: isFirstSticky ? 0 : `${leftOffset}px`,
                              zIndex: 10,
                              backgroundColor: stickyBgColor,
                              borderRight: 'none', // No vertical line between POS and Player
                              borderBottom: 'none',
                              boxShadow: isLastSticky
                                ? `2px 0 4px ${withAlpha(C.black, 0.3)}` // Shadow at frozen/scroll boundary
                                : 'none',
                              marginLeft: 0,
                              paddingLeft: 0,
                              paddingRight: 0,
                              paddingTop: 0,
                              paddingBottom: 0,
                              opacity: 1,
                              backgroundClip: 'padding-box',
                              overflow: 'hidden' // Clip to prevent content showing through
                            } : {
                              // NO box-shadow - yellow line is on <tr> border-bottom for full width
                              boxShadow: 'none'
                            })
                          }}
                        >
                          {isStickyColumn ? (
                            <div 
                              data-sticky-cell="true"
                              data-row-type={rowType}
                              data-is-accrued={isAccruedRow}
                              style={{
                                backgroundColor: stickyBgColor,
                                width: '100%',
                                height: '22px', // Fixed height to match regular cells
                                minHeight: '22px', // Ensure minimum height
                                maxHeight: '22px', // Prevent expansion
                                margin: 0,
                                paddingTop: '1px', // Minimal padding to fit 24px height
                                paddingBottom: '1px', // Minimal padding to fit 24px height
                                paddingLeft: 0, // NO left padding
                                paddingRight: isSecondSticky ? '4px' : '0px',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0, // Extend fully to bottom
                                zIndex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: isSecondSticky ? 'flex-start' : 'center',
                                marginLeft: 0, // Explicitly no left margin
                                // NO box-shadow - yellow line is on <tr> border-bottom for full width
                                boxShadow: 'none'
                              }}>
                              {cellContent}
                            </div>
                          ) : cellContent}
                        </td>
                      );
                    })}
                  </tr>
                );
              });
            });
          })()}
          
          {/* Totals Row — a NORMAL in-flow last <tr> (NOT sticky/overlay). It
              scrolls with the table like every other row and is formatted like the
              HEADER row: header background + the gold header text treatment
              (headerStyle), with the gold header underline mirrored as a top
              border. The first column stays frozen for HORIZONTAL scroll only
              (no sticky-bottom). */}
          {showTotals && totalsRow && (() => {
            const totalsHeaderBg = borderless ? mobileTableStyles.borderless.header : mobileTableStyles.header.background;
            const totalsTextStyle = headerStyle || { color: C.white, fontWeight: 'bold' };
            return (
            <tr style={{
              backgroundColor: totalsHeaderBg,
              borderTop: `1px solid ${dynastyTheme.tokens.colors.primary}`,
            }}>
              {columns.map((column, colIdx) => {
                const isStickyColumn = isStickyCol(column);
                return (
                <td
                  key={`totals-${column.key || colIdx}`}
                  style={{
                    padding: column.cellPadding || '3px 4px',
                    textAlign: column.textAlign || 'center',
                    fontSize: column.fontSize || '11px',
                    width: column.width ? `${column.width}px` : 'auto',
                    background: totalsHeaderBg,
                    ...totalsTextStyle, // header formatting (gold + weight)
                    // First column frozen for HORIZONTAL scroll only — never sticky-bottom.
                    ...(isStickyColumn ? {
                      position: 'sticky',
                      left: 0,
                      zIndex: 12
                    } : {})
                  }}
                >
                  {(() => {
                    if (column.render) {
                      // Support both desktop and mobile formats
                      if (column.accessor) {
                        // Mobile format: call accessor first, then render with (value, row)
                        const rowData = { ...totalsRow, statType: 'accrued', player: null };
                        const cellValue = column.accessor(rowData);
                        return column.render(cellValue, rowData);
                      } else {
                        // Desktop format: render(player, rowType, item)
                        return column.render(null, 'accrued', totalsRow);
                      }
                    } else {
                      // No render function - use accessor or getNestedValue
                      if (column.accessor) {
                        const rowData = { ...totalsRow, statType: 'accrued', player: null };
                        return column.accessor(rowData);
                      } else {
                        return getNestedValue(totalsRow.totals, column.key);
                      }
                    }
                  })()}
                </td>
                );
              })}
            </tr>
            );
          })()}
        </tbody>
      </table>

      {/* Empty State */}
      {data.length === 0 && !showTotals && (
        <div style={{
          padding: '40px 20px',
          textAlign: 'center',
          color: dynastyTheme.tokens.colors.neutral[500]
        }}>
          No data available
        </div>
      )}
      </div>
      {/* Prop C: inline overflow affordances — › when more columns scroll right,
          ⌄ when more rows scroll down. pointer-events: none (signal only). */}
      {overflow.right && (
        <div aria-hidden="true" data-overflow="right" style={overflowArrowRightStyle}>
          <ChevronRight size={14} />
        </div>
      )}
      {overflow.down && (
        <div aria-hidden="true" data-overflow="down" style={overflowArrowDownStyle}>
          <ChevronDown size={14} />
        </div>
      )}
    </div>
  );
};

export default MobileDynastyTable;
